/**
 * 准备度状态栏组件
 * 横向排列的准备度和环境参数状态栏
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.6, 3.7
 */

import { useTranslation } from "react-i18next";
import { CheckCircle2, Thermometer, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TargetTemperature } from "@/types/care-plan";

interface ReadinessBarProps {
  score: number;
  targetTemperature?: TargetTemperature;
  isUnlocked: boolean;
  className?: string;
}

export function ReadinessBar({
  score,
  targetTemperature,
  isUnlocked,
  className,
}: ReadinessBarProps) {
  const { t } = useTranslation("assessments");

  const getScoreStyle = (s: number) => {
    if (s >= 80) return "text-green-600 bg-green-50 dark:bg-green-950/30";
    if (s >= 50) return "text-amber-600 bg-amber-50 dark:bg-amber-950/30";
    return "text-amber-600 bg-amber-50 dark:bg-amber-950/30"; // 使用 amber 替代 red
  };

  const getEncouragementText = (s: number) => {
    if (s >= 80) return t("carePlan.readinessReady");
    if (s >= 50) return t("carePlan.readinessPartial");
    return t("carePlan.readinessLow");
  };

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {/* 准备度指示器 */}
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium min-h-[44px]",
          getScoreStyle(score)
        )}
      >
        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        <span>
          {t("carePlan.readiness")} {score}%
        </span>
        {score >= 80 && (
          <span className="text-xs opacity-80">
            {getEncouragementText(score)}
          </span>
        )}
      </div>

      {/* 目标水温 */}
      {targetTemperature && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted text-sm min-h-[44px]">
          <Thermometer className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span>
            {t("carePlan.targetTemp")}{" "}
            {isUnlocked && targetTemperature.display ? (
              targetTemperature.display
            ) : (
              <span className="inline-flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {targetTemperature.preview_display}
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

export default ReadinessBar;
